/**
 * usePermissions Hook
 * Provides permission checking utilities for sub-admin users
 * Reads directly from localStorage to avoid React state timing issues
 */

import { useCallback } from "react";

export const usePermissions = () => {
  /**
   * Get current permissions from localStorage
   */
  const getPermissionsData = useCallback(() => {
    // Try both possible keys for userType (uppercase from StorageConstant, lowercase from direct set)
    const userType = localStorage.getItem("userType") || localStorage.getItem("USER_TYPE");
    let assignedMenus = [];
    
    try {
      const storedMenus = localStorage.getItem("assignedMenus");
      assignedMenus = storedMenus ? JSON.parse(storedMenus) : [];
    } catch {
      assignedMenus = [];
    }
    
    return { userRole: userType?.toUpperCase() || "", assignedMenus };
  }, []);

  /**
   * Check if user has a specific permission for a menu
   */
  const hasPermission = useCallback((menuKey, permission) => {
    const { userRole, assignedMenus } = getPermissionsData();
    
    // Full admin has all permissions - no need to check assignedMenus
    if (userRole === "ADMIN") {
      return true;
    }

    // For sub-admin and seller (sellers can have admin-access roles), check assigned menus
    if (userRole === "SUB-ADMIN" || userRole === "SELLER") {
      const menuPermission = assignedMenus.find(
        (menu) => {
          if (menu.menuKey === menuKey) return true;
          if (menu.menuLink?.includes(menuKey)) return true;
          if (menuKey.startsWith("failed-orders")) return false;
          if (menuKey.includes(menu.menuKey) || menu.menuKey?.includes(menuKey)) return true;
          return false;
        }
      );
      return menuPermission ? menuPermission[permission] === true : false;
    }

    if (userRole && userRole !== "SELLER") return true;
    return false;
  }, [getPermissionsData]);

  /**
   * Shorthand permission checks
   */
  const canView = useCallback((menuKey) => hasPermission(menuKey, "canView"), [hasPermission]);
  const canCreate = useCallback((menuKey) => hasPermission(menuKey, "canCreate"), [hasPermission]);
  const canEdit = useCallback((menuKey) => hasPermission(menuKey, "canEdit"), [hasPermission]);
  const canDelete = useCallback((menuKey) => hasPermission(menuKey, "canDelete"), [hasPermission]);

  /**
   * Check if user is full admin
   */
  const isAdmin = useCallback(() => {
    const { userRole } = getPermissionsData();
    return userRole === "ADMIN";
  }, [getPermissionsData]);

  /**
   * Check if user is sub-admin
   */
  const isSubAdmin = useCallback(() => {
    const { userRole } = getPermissionsData();
    return userRole === "SUB-ADMIN";
  }, [getPermissionsData]);

  /**
   * Get assigned menus
   */
  const getAssignedMenus = useCallback(() => {
    const { assignedMenus } = getPermissionsData();
    return assignedMenus;
  }, [getPermissionsData]);

  return {
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isAdmin,
    isSubAdmin,
    assignedMenus: getAssignedMenus(),
  };
};

export default usePermissions;
